import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n';
import { useLanguageStore } from '@/stores/language.store';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Languages } from 'lucide-react';
import { Button } from './button';

interface LanguageSelectorProps {
  /** When true, saves the preference via API on change (use in Settings page). Default: false. */
  persist?: boolean;
  /** Extra CSS classes for the trigger button. */
  className?: string;
}

export function LanguageSelector({ persist = false, className }: LanguageSelectorProps) {
  const { language, setLanguage } = useLanguageStore();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          title={LANGUAGE_LABELS[language as SupportedLanguage] ?? language}
          aria-label="Select language"
        >
          <Languages className="h-4 w-4" />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[160px] rounded-md border border-border bg-popover p-1 shadow-md text-sm text-popover-foreground"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <DropdownMenu.Item
              key={lang}
              onSelect={() => setLanguage(lang, persist)}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent"
            >
              <span
                className={
                  lang === language ? 'font-medium text-foreground' : 'text-muted-foreground'
                }
              >
                {LANGUAGE_LABELS[lang]}
              </span>
              {lang === language && <span className="ml-auto text-xs text-primary">✓</span>}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
